import { ErrorEnum } from 'js/src/client';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { AppDispatch } from 'js/src/libs/RootState';
import { isApiErrorWithProperty } from 'js/src/types';

// Matches the backend's VIDEO_LIMIT_FEATURE_NAME (web/video_limit.py). Feature-usage-limit 403s
// carry a `feature_name`; we scope to this one so other limits (e.g. AI voiceover credits) that
// share the `hit-feature-usage-limit` error_code don't trigger the video-limit upgrade modal.
const VIDEO_LIMIT_FEATURE_NAME = 'video_limit';

/**
 * True when `err` is the backend's "free-plan video limit reached" rejection (a 403 with the
 * feature-usage-limit payload for the video-count limit). The frontend's proactive
 * {@link useVideoLimitGate} check can be stale (another tab, or videos created earlier this
 * session), so the backend 403 is the real source of truth — use this to recognize it.
 */
export function isVideoLimitError(err: unknown): boolean {
    return (
        isApiErrorWithProperty(err, 'error_code') &&
        err.body.error_code === ErrorEnum.HIT_FEATURE_USAGE_LIMIT &&
        err.body.feature_name === VIDEO_LIMIT_FEATURE_NAME
    );
}

/**
 * If `err` is the video-limit rejection, show the upgrade modal and return `true`. Otherwise return
 * `false` so the caller can fall back to its normal error handling. Use this in the `catch` of any
 * video-creating request so a stale client surfaces the upsell instead of a generic error.
 */
export function handleVideoLimitError(err: unknown, dispatch: AppDispatch): boolean {
    if (!isVideoLimitError(err)) {
        return false;
    }
    dispatch(
        showModal({
            modal_id: MODAL_IDS.FEATURE_LOCK,
            modal_context: ImmutableFromJS({ featureName: LOCKED_FEATURES.VIDEO_LIMIT }),
        }),
    );
    return true;
}
